import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, Download, RefreshCw } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'
import { Panel, SectionHeader } from '@/components/erp/primitives'
import { FilterBar } from '@/shared/ui/filters'
import { ErrorStrip, Forbidden, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { explain } from '@/shared/api/problem'
import { fmtNumber } from '@/shared/lib/format'
import type { AnalyticsEnvelope } from '@/shared/api/types'
import { getExportJob, listPrograms, recomputeAnalytics, requestExport, type AnalyticsParams } from '../api'
import { EnvelopeBar } from '../parts'
import { CATEGORY_ORDER, PROGRAM_STATUS_ORDER, categoryLabel, programStatusLabel } from '../vocab'

/**
 * The shell every analytics screen shares.
 *
 * ## One filter object across all twelve
 *
 * A filter that means something different on two screens is how two screens come
 * to disagree about the same programme. The filter lives in the query string, so
 * a link to "youth programmes in Kisumu" is a link somebody can send.
 *
 * ## The envelope is not decoration
 *
 * Every response carries `asOfDate`, `computedAt` and — the one that matters —
 * `scopingFamily`. A ward coordinator sees a national programme's WHOLE totals
 * under PROGRAMME scope and only their own ward's rows under DELIVERY. Both are
 * right. A reader who does not know which they are looking at will put the two
 * in one sentence, so the badge is on every screen, always.
 */

export interface AnalyticsFilterState {
  status: string[]
  category: string[]
  unitTypeCode: string
}

export function useAnalyticsFilters() {
  const [params, setParams] = useSearchParams()
  const status = params.getAll('status')
  const category = params.getAll('category')
  const unitTypeCode = params.get('unitTypeCode') ?? 'WARD'

  /**
   * The QUERY-STRING names, which are not the record's names.
   *
   * `ProgramAnalyticsController.Filters` is a bean bound from the request, and
   * its setters are `setStatus`/`setCategory`. It converts to an
   * `AnalyticsFilter` record whose components are `statusCodes`/`categoryCodes`
   * — the names of the internal type, never of the wire. Sending the record's
   * names is not an error the server reports: Spring binds nothing, the filter
   * arrives empty and every screen answers about the whole portfolio while the
   * chips say two statuses are selected.
   *
   * The EXPORT body is the other way round — it carries an `AnalyticsFilter`
   * directly, so `requestExport` sends the record's names. They are genuinely
   * different, which is exactly why both are written down here.
   */
  const query = useMemo(() => ({
    status: status.length ? status : undefined,
    category: category.length ? category : undefined,
    unitTypeCode,
  }), [status.join(','), category.join(','), unitTypeCode])

  const exportFilter = useMemo(() => ({
    statusCodes: status.length ? status : undefined,
    categoryCodes: category.length ? category : undefined,
    unitTypeCode,
  }), [status.join(','), category.join(','), unitTypeCode])

  const toggle = (key: 'status' | 'category', value: string) => {
    const sp = new URLSearchParams(params)
    const current = sp.getAll(key)
    sp.delete(key)
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
    next.forEach((v) => sp.append(key, v))
    setParams(sp, { replace: true })
  }

  const setUnitType = (v: string) => {
    const sp = new URLSearchParams(params)
    sp.set('unitTypeCode', v)
    setParams(sp, { replace: true })
  }

  const clear = () => setParams(new URLSearchParams(), { replace: true })

  return { status, category, unitTypeCode, query, exportFilter, toggle, setUnitType, clear }
}

export function AnalyticsFilterBar({ showUnitType }: { showUnitType?: boolean }) {
  const f = useAnalyticsFilters()
  return (
    <FilterBar>
      <MultiChips label="Status" options={PROGRAM_STATUS_ORDER.map((s) => ({
        value: s, label: programStatusLabel(s),
      }))} selected={f.status} onToggle={(v) => f.toggle('status', v)} />
      <MultiChips label="Category" options={CATEGORY_ORDER.map((c) => ({
        value: c, label: categoryLabel(c),
      }))} selected={f.category} onToggle={(v) => f.toggle('category', v)} />
      {showUnitType && (
        <select className="erp-input" aria-label="Unit level"
          value={f.unitTypeCode} onChange={(e) => f.setUnitType(e.target.value)}>
          <option value="COUNTY">County</option>
          <option value="CONSTITUENCY">Constituency</option>
          <option value="WARD">Ward</option>
          <option value="POLLING_STATION">Polling station</option>
        </select>
      )}
      {(f.status.length > 0 || f.category.length > 0) && (
        <button type="button" className="erp-btn-ghost h-9 text-erp-sm text-chocolate" onClick={f.clear}>
          Clear filters
        </button>
      )}
    </FilterBar>
  )
}

function MultiChips({ label, options, selected, onToggle }: {
  label: string
  options: { value: string; label: string }[]
  selected: string[]
  onToggle: (v: string) => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button type="button" className="erp-input h-9 text-left min-w-[9rem]"
        onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {label}{selected.length ? ` · ${selected.length}` : ''}
      </button>
      {open && (
        <>
          <button type="button" className="fixed inset-0 z-10" aria-label="Close"
            onClick={() => setOpen(false)} />
          <div className="absolute z-20 mt-1 w-56 rounded-md border border-border bg-background shadow-lg p-1 max-h-72 overflow-auto">
            {options.map((o) => (
              <label key={o.value}
                className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted cursor-pointer text-erp-xs">
                <input type="checkbox" checked={selected.includes(o.value)}
                  onChange={() => onToggle(o.value)} />
                {o.label}
              </label>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/**
 * Loads one analytics endpoint and renders the envelope, the error and the
 * export control around it.
 */
export function AnalyticsScreen<T extends Partial<AnalyticsEnvelope>>({
  title, description, load, exportKind, showUnitType, isEmpty, children, deps = [],
}: {
  title: string
  description: string
  load: (filter: AnalyticsParams) => Promise<T>
  exportKind?: string
  showUnitType?: boolean
  /**
   * "This screen has nothing to show." Every analytics endpoint reads
   * `program_analytics_rollup`, which is populated by the nightly job — so on a
   * system where that job has never run, `overview` returns `programmes: 0` for
   * a portfolio with fifteen programmes in it, stamped `asOfDate` = today and
   * `computedAt` = now. The envelope asserts a computation that never happened.
   *
   * Rendering that as "Programmes 0" is the same lie as rendering an unmeasured
   * uplift as 0%. So each screen says when it is empty, and the shell works out
   * WHY — no programmes at all, or a rollup nobody has computed.
   */
  isEmpty?: (data: T) => boolean
  /** Called WITH the current filter — a screen that ignores it renders the whole
   *  portfolio under a filter bar that says otherwise. */
  children: (data: T, reload: () => void) => ReactNode
  deps?: unknown[]
}) {
  const canRead = useCan('program:analytics')
  const canExport = useCan('program:export')
  const f = useAnalyticsFilters()

  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [tick, setTick] = useState(0)
  const [exporting, setExporting] = useState(false)
  const [exportNote, setExportNote] = useState<string | null>(null)
  const [exportLink, setExportLink] = useState<string | null>(null)
  // The export loop can outlive the screen — a poll runs for up to thirty
  // seconds and the reader is free to move on. Without this it writes state into
  // a component that is gone.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => { mounted.current = false }
  }, [])

  useEffect(() => {
    let alive = true
    setLoading(true)
    load(f.query)
      .then((d) => { if (alive) { setData(d); setError(null) } })
      .catch((e) => { if (alive) { setError(explain(e)); setData(null) } })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(f.query), tick, ...deps])

  /**
   * Request the export, then WAIT for it.
   *
   * `POST /export` returns a job in `QUEUED` with a null `rowCount` and a null
   * `downloadUrl`. Announcing that as "Export ready — undefined rows" is not
   * only wrong copy: it tells somebody their file is waiting for them when
   * nothing has been written yet.
   */
  const doExport = async () => {
    if (!exportKind) return
    setExporting(true); setExportNote('Export queued…'); setExportLink(null)
    try {
      let job = await requestExport(exportKind, f.exportFilter)
      // Bounded, because a stuck job must end in a sentence rather than in a
      // spinner that outlives the reader's patience.
      for (let i = 0; i < 30 && (job.statusCode === 'QUEUED' || job.statusCode === 'RUNNING'); i++) {
        await new Promise((r) => setTimeout(r, 1000))
        if (!mounted.current) return
        job = await getExportJob(job.jobId)
        if (!mounted.current) return
        setExportNote(job.statusCode === 'RUNNING' ? 'Building the file…' : 'Export queued…')
      }
      if (!mounted.current) return
      if (job.statusCode === 'FAILED') {
        setExportNote('The export failed. Nothing was written — try again, or narrow the filter.')
      } else if (job.statusCode === 'COMPLETED') {
        setExportNote(job.truncated
          ? `Export ready — ${fmtNumber(job.rowCount ?? 0)} rows, TRUNCATED at the row cap. The file's first line says so too.`
          : `Export ready — ${fmtNumber(job.rowCount ?? 0)} rows.`)
        // A LINK, not `window.open`. By the time the poll finishes the click's
        // user-activation token is long gone and the browser blocks the popup —
        // leaving "Export ready" beside nothing happening, which reads as the
        // page lying about a file it did produce.
        setExportLink(job.downloadUrl ?? null)
      } else {
        setExportNote('The export is still running. It will appear in your exports when it finishes.')
      }
    } catch (e) {
      if (mounted.current) setExportNote(explain(e))
    } finally {
      if (mounted.current) setExporting(false)
    }
  }

  if (!canRead) return <Forbidden what="programme analytics" />

  return (
    <div className="space-y-4">
      <SectionHeader
        title={title}
        description={description}
        right={(
          <div className="flex items-center gap-2">
            <button type="button" className="erp-btn" aria-label="Refresh"
              onClick={() => setTick((t) => t + 1)}>
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
            {exportKind && canExport && (
              <button type="button" className="erp-btn" onClick={doExport} disabled={exporting}>
                <Download className="h-3.5 w-3.5" /> {exporting ? 'Exporting…' : 'Export'}
              </button>
            )}
          </div>
        )}
      />

      <AnalyticsFilterBar showUnitType={showUnitType} />

      {data && <EnvelopeBar env={data} />}
      {exportNote && (
        <p className="text-erp-xs text-muted-foreground">
          {exportNote}
          {exportLink && (
            <>
              {' '}
              <a href={exportLink} target="_blank" rel="noopener noreferrer"
                className="text-chocolate underline underline-offset-2">Download the file</a>
            </>
          )}
        </p>
      )}
      <ErrorStrip message={error} />

      {loading && !data ? <LoadingBlock label={`Loading ${title.toLowerCase()}…`} />
        : data ? (
          <>
            {isEmpty?.(data) && (
              <EmptyRollupNotice onRecomputed={() => setTick((t) => t + 1)} />
            )}
            {children(data, () => setTick((t) => t + 1))}
          </>
        )
        : null}
    </div>
  )
}

export { Panel }


/**
 * Why this screen is empty — and it is one of two very different reasons.
 *
 * Every analytics endpoint reads `program_analytics_rollup`. `latestDay()` falls
 * back to *today* when that table has never been written, `forDay(today, …)`
 * then matches nothing, and `overview` returns `programmes: 0` — for a portfolio
 * that has programmes in it. Nothing in the response distinguishes that from a
 * genuinely empty portfolio: the envelope stamps `asOfDate` = today and
 * `computedAt` = now either way.
 *
 * The client CAN tell them apart, by asking the one question the rollup is not
 * involved in: does a programme exist at all? If one does, the rollup has simply
 * never been computed for it, and the fix is one button — which is offered here
 * rather than left on the Alerts screen where nobody looking at an empty
 * dashboard would think to go.
 */
function EmptyRollupNotice({ onRecomputed }: { onRecomputed: () => void }) {
  const canRecompute = useCan('program:update')
  const [programmes, setProgrammes] = useState<number | null>(null)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    listPrograms({}, undefined, 1)
      .then((page) => { if (alive) setProgrammes(page.content.length + (page.hasMore ? 1 : 0)) })
      .catch(() => { if (alive) setProgrammes(null) })
    return () => { alive = false }
  }, [])

  const recompute = async () => {
    setBusy(true); setError(null); setNote(null)
    try {
      const r = await recomputeAnalytics()
      setNote(`Recomputed ${r.from} → ${r.to}: ${fmtNumber(r.programmesTouched)} programmes.`)
      onRecomputed()
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  // Still checking, or the check itself failed — say nothing rather than guess
  // which of the two situations this is.
  if (programmes === null) return null

  if (programmes === 0) {
    return (
      <Note tone="info">
        There are no programmes yet, so there is nothing to analyse.{' '}
        <Link to="/programs" className="underline underline-offset-2">Create one</Link> and the
        figures here will follow once the nightly rollup has run.
      </Note>
    )
  }

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900/50 dark:bg-amber-950/40"
      role="status">
      <div className="flex items-start gap-2 text-erp-sm text-amber-900 dark:text-amber-200">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" aria-hidden />
        <div className="min-w-0">
          <p className="font-medium">
            There are programmes, but the analytics rollup has not been computed for them.
          </p>
          <p className="text-erp-xs mt-0.5">
            Every screen here reads a nightly snapshot rather than the live tables, so that a
            figure quoted in a meeting is the same figure an hour later. The snapshot is empty —
            which the API cannot distinguish from an empty portfolio, so it reports zero.
            {canRecompute
              ? ' Run it now, or wait for tonight.'
              : ' It runs overnight; somebody with the programme-update authority can run it now.'}
          </p>
          {canRecompute && (
            <button type="button" className="erp-btn mt-2" disabled={busy}
              onClick={() => void recompute()}>
              <RefreshCw className={busy ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
              {busy ? 'Recomputing…' : 'Recompute now'}
            </button>
          )}
          {note && <p className="text-erp-xs mt-1.5">{note}</p>}
          {error && <p className="text-erp-xs mt-1.5 text-red-700 dark:text-red-300">{error}</p>}
        </div>
      </div>
    </div>
  )
}
